import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import * as THREE from "three";
import {
  PRESET_GRID_BOUNDARY_YAWS,
  PRESET_GRID_MARKER_FONT_PX,
  PRESET_GRID_MARKER_SIZE_PX,
  PRESET_GRID_TOTAL,
  presetGridBoundaryPitches,
  presetGridCenter360,
  presetGridIdFromYawPitch360,
} from "../../lib/presetGrid.ts";
import {
  equirectNormToPatrolHotspotDeg,
  patrolHotspotWorldPosition,
  spherePointToEquirectNorm,
} from "../ConstructionSiteManagement/patrol/patrolHotspotProjection.js";

export type HotspotPano360Marker = {
  id: string;
  yaw_deg: number;
  pitch_deg: number;
  label?: string;
  variant: "active" | "saved";
};

export type HotspotPano360ImageViewProps = {
  imageUrl: string;
  markers?: HotspotPano360Marker[];
  lookAtYawDeg?: number;
  lookAtPitchDeg?: number;
  centerViewNonce?: number;
  onEquirectPick?: (normX: number, normY: number) => void;
  onViewerRotation?: (yawDeg: number, pitchDeg: number) => void;
  onMarkerClick?: (id: string) => void;
  presetGridEnabled?: boolean;
  selectedPresetGridId?: number | null;
  onPresetGridSelect?: (gridId: number) => void;
};

const SPHERE_R = 100;
const CLICK_PX = 10;
const DRAG_SENSITIVITY = 0.28;
const FOV = 75;
const PICK_BUTTONS = new Set([0, 2]);
const PRESET_GRID_OUTLINE_STEPS = 84;
const PRESET_GRID_OUTLINE_PITCH_MIN = -80;
const PRESET_GRID_OUTLINE_PITCH_MAX = 80;
const PRESET_GRID_SEGMENT_BREAK_PX_FRAC = 0.35;

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v));
}

function gridIdFromNorm(normX: number, normY: number): number | null {
  const normalizedX = ((normX % 1) + 1) % 1;
  const normalizedY = clamp(normY, 0, 0.999999);
  const { yaw_deg } = equirectNormToPatrolHotspotDeg(normalizedX, normalizedY);
  return presetGridIdFromYawPitch360(yaw_deg, normalizedY);
}

/**
 * Ray hit → equirect UV using the same convention as `patrolHotspotDegToEquirectNorm`
 * / `patrolHotspotWorldPosition` (not Three.js triangle UVs — those drift on flipped spheres).
 */
function normsFromSphereHit(hit: {
  point: { x: number; y: number; z: number };
}) {
  return spherePointToEquirectNorm(hit.point.x, hit.point.y, hit.point.z);
}

function applyMarkerStyles(
  el: HTMLButtonElement,
  variant: "active" | "saved" | "preset",
  selected = false,
) {
  if (variant === "preset") {
    Object.assign(el.style, {
      position: "absolute",
      borderRadius: "50%",
      border: selected
        ? "2px solid #FACC15"
        : "1px solid rgba(255,255,255,0.9)",
      background: selected ? "rgba(37,99,235,0.96)" : "rgba(15,23,42,0.8)",
      color: "#fff",
      fontWeight: "700",
      fontSize: `${PRESET_GRID_MARKER_FONT_PX}px`,
      lineHeight: "1",
      padding: "0",
      boxShadow: "0 0 0 2px rgba(0,0,0,0.35)",
      pointerEvents: "auto",
      cursor: "pointer",
      zIndex: "4",
    });
    return;
  }
  const isActive = variant === "active";
  Object.assign(el.style, {
    position: "absolute",
    borderRadius: "50%",
    border: `2px ${isActive ? "dashed" : "solid"} ${isActive ? "#FBBF24" : "#fff"}`,
    background: isActive ? "rgba(17,24,39,0.85)" : "rgba(37,99,235,0.9)",
    color: "#fff",
    fontWeight: "700",
    fontSize: isActive ? "16px" : "11px",
    lineHeight: "1",
    padding: "0",
    boxShadow: "0 0 0 2px rgba(0,0,0,0.35)",
    pointerEvents: "auto",
    cursor: "pointer",
    zIndex: isActive ? "3" : "2",
  });
}

export function HotspotPano360ImageView({
  imageUrl,
  markers = [],
  lookAtYawDeg = 0,
  lookAtPitchDeg = 0,
  centerViewNonce = 0,
  onEquirectPick,
  onViewerRotation,
  onMarkerClick,
  presetGridEnabled = false,
  selectedPresetGridId = null,
  onPresetGridSelect,
}: HotspotPano360ImageViewProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onPickRef = useRef(onEquirectPick);
  const onViewerRef = useRef(onViewerRotation);
  const onMarkerClickRef = useRef(onMarkerClick);
  const presetGridEnabledRef = useRef(presetGridEnabled);
  const selectedPresetGridIdRef = useRef(selectedPresetGridId);
  const onPresetGridSelectRef = useRef(onPresetGridSelect);
  const markersRef = useRef(markers);
  const lookAtRef = useRef({ yaw: lookAtYawDeg, pitch: lookAtPitchDeg });
  const centerNonceRef = useRef(centerViewNonce);
  const snapViewRef = useRef(false);
  const viewRef = useRef({ yaw: lookAtYawDeg, pitch: lookAtPitchDeg });

  onPickRef.current = onEquirectPick;
  onViewerRef.current = onViewerRotation;
  onMarkerClickRef.current = onMarkerClick;
  presetGridEnabledRef.current = presetGridEnabled;
  selectedPresetGridIdRef.current = selectedPresetGridId;
  onPresetGridSelectRef.current = onPresetGridSelect;
  markersRef.current = markers;
  lookAtRef.current = { yaw: lookAtYawDeg, pitch: lookAtPitchDeg };
  viewRef.current = { yaw: lookAtYawDeg, pitch: lookAtPitchDeg };
  if (centerNonceRef.current !== centerViewNonce) {
    centerNonceRef.current = centerViewNonce;
    snapViewRef.current = true;
  }

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !imageUrl) return undefined;

    let destroyed = false;
    let raf = 0;

    const markerLayer = document.createElement("div");
    Object.assign(markerLayer.style, {
      position: "absolute",
      inset: "0",
      pointerEvents: "none",
      zIndex: "2",
    });
    host.appendChild(markerLayer);

    const presetGridOutlineSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    Object.assign(presetGridOutlineSvg.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: "2",
      display: "none",
    });
    const presetGridOutlineYawPaths = PRESET_GRID_BOUNDARY_YAWS.map(() => {
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "rgba(255,255,255,0.55)");
      path.setAttribute("stroke-width", "1.5");
      path.setAttribute("stroke-dasharray", "4 5");
      presetGridOutlineSvg.appendChild(path);
      return path;
    });
    const presetGridOutlinePitchPaths = presetGridBoundaryPitches().map(() => {
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "rgba(255,255,255,0.55)");
      path.setAttribute("stroke-width", "1.5");
      path.setAttribute("stroke-dasharray", "4 5");
      presetGridOutlineSvg.appendChild(path);
      return path;
    });
    host.appendChild(presetGridOutlineSvg);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(FOV, 1, 0.01, 200);
    camera.position.set(0, 0, 0);
    camera.rotation.order = "YXZ";

    const geometry = new THREE.SphereGeometry(SPHERE_R, 120, 80);
    geometry.scale(-1, 1, 1);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      toneMapped: false,
    });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    let viewerYaw = lookAtRef.current.yaw;
    let viewerPitch = lookAtRef.current.pitch;

    const applyCameraRotation = () => {
      camera.rotation.y = THREE.MathUtils.degToRad(viewerYaw);
      camera.rotation.x = THREE.MathUtils.degToRad(
        Math.max(-89, Math.min(89, viewerPitch)),
      );
    };
    applyCameraRotation();

    const loader = new THREE.TextureLoader();
    loader.load(
      imageUrl,
      (tex) => {
        if (destroyed) {
          tex.dispose();
          return;
        }
        tex.flipY = true;
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        material.map = tex;
        material.color.setHex(0xffffff);
        material.needsUpdate = true;
      },
      undefined,
      () => undefined,
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const canvas = renderer.domElement;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    canvas.style.cursor = "grab";
    canvas.style.touchAction = "none";
    host.insertBefore(canvas, markerLayer);

    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const projected = new THREE.Vector3();
    const camForward = new THREE.Vector3();
    const sphereDir = new THREE.Vector3();
    const outlineWorld = new THREE.Vector3();
    const markerEls = new Map<string, HTMLButtonElement>();

    const clientToNdc = (clientX: number, clientY: number) => {
      const r = canvas.getBoundingClientRect();
      const w = Math.max(r.width, 1e-9);
      const h = Math.max(r.height, 1e-9);
      return {
        x: ((clientX - r.left) / w) * 2 - 1,
        y: -((clientY - r.top) / h) * 2 + 1,
      };
    };

    const samplePickNormFromNdc = (ndcCx: number, ndcCy: number) => {
      const stepX = (3 * 2) / Math.max(canvas.clientWidth, 1);
      const stepY = (3 * 2) / Math.max(canvas.clientHeight, 1);
      let sumSin = 0;
      let sumCos = 0;
      let sumY = 0;
      let n = 0;
      for (let j = -1; j <= 1; j++) {
        for (let i = -1; i <= 1; i++) {
          ndc.x = clamp(ndcCx + i * stepX, -1, 1);
          ndc.y = clamp(ndcCy + j * stepY, -1, 1);
          raycaster.setFromCamera(ndc, camera);
          const hit = raycaster.intersectObject(mesh, false)[0];
          if (!hit?.point) continue;
          const norms = normsFromSphereHit(hit);
          if (!norms) continue;
          const ang = norms.normX * Math.PI * 2;
          sumSin += Math.sin(ang);
          sumCos += Math.cos(ang);
          sumY += norms.normY;
          n++;
        }
      }
      if (n === 0) return null;
      let phiMean = Math.atan2(sumSin / n, sumCos / n);
      if (phiMean < 0) phiMean += Math.PI * 2;
      return {
        normX: clamp(phiMean / (Math.PI * 2), 0, 1),
        normY: clamp(sumY / n, 0, 1),
      };
    };

    const pickAtClient = (clientX: number, clientY: number) => {
      if (!onPickRef.current) return;
      camera.updateMatrixWorld(true);
      mesh.updateMatrixWorld(true);
      const p = clientToNdc(clientX, clientY);
      const norms = samplePickNormFromNdc(p.x, p.y);
      if (!norms) return;
      if (presetGridEnabledRef.current) {
        const gridId = gridIdFromNorm(norms.normX, norms.normY);
        if (gridId !== null) onPresetGridSelectRef.current?.(gridId);
        return;
      }
      onPickRef.current(norms.normX, norms.normY);
    };

    const projectWorldToHostPct = (world: THREE.Vector3) => {
      projected.copy(world).project(camera);
      const canvasR = canvas.getBoundingClientRect();
      const hostR = host.getBoundingClientRect();
      const xInCanvas = (projected.x * 0.5 + 0.5) * canvasR.width;
      const yInCanvas = (-projected.y * 0.5 + 0.5) * canvasR.height;
      const left = canvasR.left - hostR.left + xInCanvas;
      const top = canvasR.top - hostR.top + yInCanvas;
      const hostW = Math.max(hostR.width, 1e-9);
      const hostH = Math.max(hostR.height, 1e-9);
      return {
        xPct: (left / hostW) * 100,
        yPct: (top / hostH) * 100,
      };
    };

    const syncMarkerDom = () => {
      const baseMarkers = markersRef.current.map((m) => ({
        ...m,
        kind: "hotspot" as const,
      }));
      const presetMarkers = presetGridEnabledRef.current
        ? Array.from({ length: PRESET_GRID_TOTAL }, (_, idx) => idx + 1).map(
            (gridId) => {
              const { yawDeg, pitchDeg } = presetGridCenter360(gridId);
              return {
                id: `__preset_grid_${gridId}`,
                yaw_deg: yawDeg,
                pitch_deg: pitchDeg,
                label: `Grid ${gridId}`,
                variant: "preset" as const,
                gridId,
                kind: "preset" as const,
              };
            },
          )
        : [];
      const list = [...baseMarkers, ...presetMarkers];
      camera.updateMatrixWorld(true);
      camera.getWorldDirection(camForward);

      const ids = new Set(list.map((m) => m.id));
      for (const id of [...markerEls.keys()]) {
        if (!ids.has(id)) {
          markerEls.get(id)?.remove();
          markerEls.delete(id);
        }
      }

      for (const m of list) {
        const world = new THREE.Vector3();
        patrolHotspotWorldPosition(m.yaw_deg, m.pitch_deg, SPHERE_R, world);
        sphereDir.copy(world).normalize();
        const visibleThreshold = m.kind === "preset" ? 0 : 0.04;
        const visible = sphereDir.dot(camForward) > visibleThreshold;

        let el = markerEls.get(m.id);
        if (!el) {
          el = document.createElement("button");
          el.type = "button";
          applyMarkerStyles(
            el,
            m.variant,
            m.kind === "preset" && selectedPresetGridIdRef.current === m.gridId,
          );
          el.addEventListener("click", (ev) => {
            ev.stopPropagation();
            if (m.kind === "preset") {
              onPresetGridSelectRef.current?.(m.gridId);
              return;
            }
            onMarkerClickRef.current?.(m.id);
          });
          markerLayer.appendChild(el);
          markerEls.set(m.id, el);
        } else {
          applyMarkerStyles(
            el,
            m.variant,
            m.kind === "preset" && selectedPresetGridIdRef.current === m.gridId,
          );
        }
        el.title = m.label ?? "";
        if (m.kind === "preset") {
          el.textContent = String(m.gridId);
        } else {
          el.textContent = m.variant === "active" ? "+" : "•";
        }
        if (m.label) el.setAttribute("aria-label", m.label);

        if (!visible) {
          el.style.display = "none";
          continue;
        }
        const { xPct, yPct } = projectWorldToHostPct(world);
        const size =
          m.kind === "preset"
            ? PRESET_GRID_MARKER_SIZE_PX
            : m.variant === "active"
              ? 28
              : 22;
        el.style.display = "block";
        el.style.left = `${xPct}%`;
        el.style.top = `${yPct}%`;
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.transform = "translate(-50%, -50%)";
      }
    };

    const syncPresetGridOutline = () => {
      if (!presetGridEnabledRef.current) {
        presetGridOutlineSvg.style.display = "none";
        return;
      }
      const hostW = Math.max(host.clientWidth, 1);
      const hostH = Math.max(host.clientHeight, 1);
      presetGridOutlineSvg.setAttribute("width", String(hostW));
      presetGridOutlineSvg.setAttribute("height", String(hostH));
      presetGridOutlineSvg.style.display = "block";

      camera.updateMatrixWorld(true);
      camera.getWorldDirection(camForward);
      const breakPx = hostW * PRESET_GRID_SEGMENT_BREAK_PX_FRAC;

      PRESET_GRID_BOUNDARY_YAWS.forEach((yawDeg, idx) => {
        const path = presetGridOutlineYawPaths[idx];
        if (!path) return;
        const segments: { x: number; y: number }[][] = [];
        let current: { x: number; y: number }[] = [];

        for (let i = 0; i <= PRESET_GRID_OUTLINE_STEPS; i++) {
          const t = i / PRESET_GRID_OUTLINE_STEPS;
          const pitchDeg =
            PRESET_GRID_OUTLINE_PITCH_MIN +
            (PRESET_GRID_OUTLINE_PITCH_MAX - PRESET_GRID_OUTLINE_PITCH_MIN) * t;
          patrolHotspotWorldPosition(yawDeg, pitchDeg, SPHERE_R, outlineWorld);
          sphereDir.copy(outlineWorld).normalize();
          if (sphereDir.dot(camForward) <= 0.04) {
            if (current.length) {
              segments.push(current);
              current = [];
            }
            continue;
          }

          const { xPct, yPct } = projectWorldToHostPct(outlineWorld);
          const pt = { x: (xPct / 100) * hostW, y: (yPct / 100) * hostH };
          if (current.length) {
            const prev = current[current.length - 1]!;
            if (Math.hypot(pt.x - prev.x, pt.y - prev.y) > breakPx) {
              segments.push(current);
              current = [pt];
              continue;
            }
          }
          current.push(pt);
        }
        if (current.length) segments.push(current);

        const d = segments
          .map((pts) =>
            pts
              .map(
                (p, pointIdx) =>
                  `${pointIdx === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
              )
              .join(" "),
          )
          .join(" ");
        path.setAttribute("d", d);
        path.style.display = d ? "block" : "none";
      });

      presetGridBoundaryPitches().forEach((pitchDeg, idx) => {
        const path = presetGridOutlinePitchPaths[idx];
        if (!path) return;
        const segments: { x: number; y: number }[][] = [];
        let current: { x: number; y: number }[] = [];

        for (let i = 0; i <= PRESET_GRID_OUTLINE_STEPS; i++) {
          const t = i / PRESET_GRID_OUTLINE_STEPS;
          const yawDeg = -180 + t * 360;
          patrolHotspotWorldPosition(yawDeg, pitchDeg, SPHERE_R, outlineWorld);
          sphereDir.copy(outlineWorld).normalize();
          if (sphereDir.dot(camForward) <= 0.04) {
            if (current.length) {
              segments.push(current);
              current = [];
            }
            continue;
          }

          const { xPct, yPct } = projectWorldToHostPct(outlineWorld);
          const pt = { x: (xPct / 100) * hostW, y: (yPct / 100) * hostH };
          if (current.length) {
            const prev = current[current.length - 1]!;
            if (Math.hypot(pt.x - prev.x, pt.y - prev.y) > breakPx) {
              segments.push(current);
              current = [pt];
              continue;
            }
          }
          current.push(pt);
        }
        if (current.length) segments.push(current);

        const d = segments
          .map((pts) =>
            pts
              .map(
                (p, pointIdx) =>
                  `${pointIdx === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
              )
              .join(" "),
          )
          .join(" ");
        path.setAttribute("d", d);
        path.style.display = d ? "block" : "none";
      });
    };

    let ptrDown = false;
    let dragRotate = false;
    let activePointerButton = 0;
    let sx = 0;
    let sy = 0;
    let lastX = 0;
    let lastY = 0;

    const onContextMenu = (e: Event) => {
      e.preventDefault();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!PICK_BUTTONS.has(e.button)) return;
      ptrDown = true;
      dragRotate = false;
      activePointerButton = e.button;
      sx = lastX = e.clientX;
      sy = lastY = e.clientY;
      canvas.style.cursor = "grabbing";
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!ptrDown || activePointerButton !== 0) return;
      const dx0 = e.clientX - sx;
      const dy0 = e.clientY - sy;
      if (!dragRotate && dx0 * dx0 + dy0 * dy0 > CLICK_PX * CLICK_PX) {
        dragRotate = true;
        lastX = e.clientX;
        lastY = e.clientY;
        return;
      }
      if (!dragRotate) return;
      const mdx = e.clientX - lastX;
      const mdy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      viewerYaw += mdx * DRAG_SENSITIVITY;
      viewerPitch = Math.max(
        -89,
        Math.min(89, viewerPitch + mdy * DRAG_SENSITIVITY),
      );
      onViewerRef.current?.(viewerYaw, viewerPitch);
      applyCameraRotation();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!ptrDown) return;
      const btn = activePointerButton;
      ptrDown = false;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (PICK_BUTTONS.has(btn) && PICK_BUTTONS.has(e.button) && !dragRotate) {
        pickAtClient(e.clientX, e.clientY);
      }
      dragRotate = false;
      activePointerButton = 0;
      canvas.style.cursor = "grab";
    };

    const onPointerCancel = () => {
      ptrDown = false;
      dragRotate = false;
      activePointerButton = 0;
      canvas.style.cursor = "grab";
    };

    canvas.addEventListener("contextmenu", onContextMenu);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerCancel);

    const resize = () => {
      const w = host.clientWidth;
      const h = host.clientHeight;
      if (w <= 0 || h <= 0) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };

    const ro = new ResizeObserver(() => resize());
    ro.observe(host);
    resize();

    const tick = () => {
      if (destroyed) return;
      const target = viewRef.current;
      if (snapViewRef.current) {
        snapViewRef.current = false;
        viewerYaw = target.yaw;
        viewerPitch = target.pitch;
        applyCameraRotation();
      } else if (!ptrDown && !dragRotate) {
        const dy = target.yaw - viewerYaw;
        const dp = target.pitch - viewerPitch;
        if (Math.abs(dy) > 0.05 || Math.abs(dp) > 0.05) {
          viewerYaw += dy * 0.22;
          viewerPitch += dp * 0.22;
          applyCameraRotation();
        } else {
          viewerYaw = target.yaw;
          viewerPitch = target.pitch;
          applyCameraRotation();
        }
      }
      renderer.render(scene, camera);
      syncPresetGridOutline();
      syncMarkerDom();
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("contextmenu", onContextMenu);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      markerEls.forEach((el) => el.remove());
      markerEls.clear();
      markerLayer.remove();
      presetGridOutlineSvg.remove();
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      if (material.map) material.map.dispose();
      canvas.remove();
    };
  }, [imageUrl]);

  return (
    <Box
      ref={hostRef}
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        bgcolor: "#0b1220",
      }}
    />
  );
}
